import { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Loader, Shield, ArrowRight } from '@/components/ui/Icons';
import { cn } from '@/lib/cn';

export type CaptchaState = {
  id: string;
  question: string;
  answer: string;
  expiresAt: string;
};

type Props = {
  value: CaptchaState | null;
  onChange: (next: CaptchaState | null) => void;
  /** Show a small validation error returned from the server. */
  error?: string;
  className?: string;
};

type ChallengeResponse = {
  ok: true;
  challenge: { id: string; question: string; expires_at: string };
};

/**
 * Server-issued maths/word captcha widget.
 * - One question is fetched on mount and cached as the form's local state.
 * - The user's typed answer is held in state alongside the challenge id.
 * - On submit, the parent posts { captcha_id, captcha_answer } to /api/quotes.
 * - A "New question" button refreshes the challenge if the user wants a different one.
 */
export function Captcha({ value, onChange, error, className }: Props) {
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const res = await api<ChallengeResponse>('/captcha');
      onChange({
        id: res.challenge.id,
        question: res.challenge.question,
        expiresAt: res.challenge.expires_at,
        answer: '',
      });
    } catch {
      setFetchError('Could not load the security check. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [onChange]);

  useEffect(() => {
    if (!value) void refresh();
  }, [value, refresh]);

  const setAnswer = (next: string) => {
    if (!value) return;
    onChange({ ...value, answer: next });
  };

  return (
    <div
      className={cn(
        'rounded-lg border bg-paper-50 p-4',
        error ? 'border-red-300' : 'border-paper-100',
        className,
      )}
    >
      <div className="flex items-center gap-3">
        <span className="grid h-9 w-9 flex-none place-items-center rounded-full bg-gold-400/15 text-gold-700">
          <Shield className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-700">
            Quick check
          </p>
          {loading && !value ? (
            <p className="mt-1 flex items-center gap-2 text-sm text-ink-700">
              <Loader className="h-3.5 w-3.5" /> Loading question…
            </p>
          ) : fetchError ? (
            <p className="mt-1 text-sm text-red-700">{fetchError}</p>
          ) : (
            <p className="mt-1 text-base font-medium text-navy-900">{value?.question}</p>
          )}
        </div>
        <button
          type="button"
          onClick={() => void refresh()}
          className="text-xs font-semibold text-ink-700 underline-offset-4 hover:text-gold-700 hover:underline"
          aria-label="New question"
        >
          New question
        </button>
      </div>

      <div className="mt-3 flex items-center gap-2">
        <input
          type="text"
          inputMode="text"
          autoComplete="off"
          spellCheck={false}
          value={value?.answer ?? ''}
          onChange={(e) => setAnswer(e.target.value)}
          placeholder="Your answer"
          className="block w-full rounded-md border border-paper-100 bg-paper-0 px-3 py-2.5 text-ink-900 shadow-xs focus:border-gold-400 focus:outline-hidden focus:ring-2 focus:ring-gold-200"
          aria-invalid={!!error}
          aria-describedby={error ? 'captcha-error' : undefined}
        />
        <ArrowRight className="hidden h-4 w-4 text-gold-500 sm:block" />
      </div>

      {error && (
        <p id="captcha-error" className="mt-2 text-sm text-red-700">
          {error}
        </p>
      )}
    </div>
  );
}
